import React, { useState, useEffect, useCallback } from "react";
import {
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Paper,
    SelectChangeEvent,
} from "@mui/material";
import { useRouter, useSearchParams, usePathname } from "next/navigation";
import { PeerGroupInfo, useDealership } from "@/contexts/DealershipContext";
import { Typography } from "./wrappers/Typography";
import { useTranslation } from "react-i18next";
import { translatePeerGroupLabel } from "@/utils/peerGroupLabel";
import { studyKeyOf } from "@/utils/groupCriteria";

interface PeerGroupSelectProps {
    width?: string | number;
}

const PeerGroupSelect: React.FC<PeerGroupSelectProps> = ({ width = "100%" }) => {
    const router = useRouter();
    const searchParams = useSearchParams();
    const pathname = usePathname();
    const { participantInfo, groupInfo, userInfo, setSelectedPeerGroup } = useDealership();

    const selectPeerGroups = useCallback(() => {
        const isGroupPage = pathname.startsWith("/group");
        let peerGroups: { [key: string]: PeerGroupInfo };

        if (!isGroupPage) {
            peerGroups = participantInfo?.peer_groups || {};
        } else {
            peerGroups = groupInfo?.peer_groups_table || {};
        }

        const availableGroups = Object.entries(peerGroups || {})
            .filter(([, peerGroupInfo]) => {
                const criteria = peerGroupInfo.peer_group_criteria;
                // Group pages serve their own study; dealer pages keep their existing filter.
                const studyKey = isGroupPage ? studyKeyOf(criteria) : "StudyID=24";

                if (criteria === `${studyKey};CarTruck=C` || criteria === `${studyKey};CarTruck=T`) {
                    return false;
                }

                if (peerGroupInfo.peer_group_short_description === "Your Dealership") {
                    return false;
                }

                if (isGroupPage && userInfo?.group_id) {
                    if (
                        criteria === `${studyKey};CarTruck=C;GroupID=${userInfo.group_id}` ||
                        criteria === `${studyKey};CarTruck=T;GroupID=${userInfo.group_id}`
                    ) {
                        return false;
                    }
                }

                return true;
            })
            .map(([groupKey]) => groupKey);

        const defaultPeerGroup =
            Object.entries(peerGroups || {}).find(([, info]) => info.peer_group_default)?.[0] ||
            availableGroups[0] ||
            "";

        const preSelectedGroup =
            (searchParams.get("peer_group") || "") in peerGroups
                ? searchParams.get("peer_group")!
                : defaultPeerGroup;

        return { availableGroups, preSelectedGroup, defaultPeerGroup };
    }, [participantInfo, groupInfo, userInfo, searchParams, pathname]);

    const { availableGroups, preSelectedGroup } = selectPeerGroups();
    const [selectedGroup, setSelectedGroup] = useState<string>(preSelectedGroup);

    useEffect(() => {
        const { preSelectedGroup } = selectPeerGroups();
        setSelectedPeerGroup(preSelectedGroup);
        setSelectedGroup(preSelectedGroup);
    }, [selectPeerGroups, setSelectedPeerGroup]);

    const handleChange = (event: SelectChangeEvent<string>) => {
        const newValue = event.target.value;
        const finalValue = newValue === "__clear__" ? "" : newValue;
        setSelectedGroup(finalValue);
        setSelectedPeerGroup(finalValue);

        const params = new URLSearchParams(searchParams.toString());
        if (finalValue) {
            params.set("peer_group", finalValue);
        } else {
            params.delete("peer_group");
        }
        router.push(`?${params.toString()}`);
    };

    const { t, i18n } = useTranslation();

    return (
        <Paper
            className="peer-select exec-step-2 comp-step-2"
            variant="outlined"
            sx={{ p: 2, borderRadius: 4, width }}
        >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography tx="common.comparisonGroups.peerGroupForComparison" variant="h8" component="h3" />
            </Box>
            <FormControl fullWidth margin="normal" size="small">
                <InputLabel id="peer-groups-select-label" className="screen-only">
                    {t("common.entities.group")}
                </InputLabel>
                <Select
                    labelId="peer-groups-select-label"
                    id="peer-groups-select"
                    value={selectedGroup}
                    onChange={handleChange}
                    label={t("common.entities.group")}
                    displayEmpty
                >
                    {availableGroups.map((group) => (
                        <MenuItem key={group} value={group}>
                            {/* Route the label through i18n (value stays the raw group key used for
                                matching/selection). On the French site, composite segment/urban
                                labels are recomposed from translated phrase tokens (combos joined by
                                a comma); "All Car" etc. use exact-key translations; proper names
                                (dealer groups, brands, provinces) fall back to the raw string.
                                [ES-1006 / ES-1026] */}
                            {translatePeerGroupLabel(group, t, i18n.language)}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        </Paper>
    );
};

export default PeerGroupSelect;
